/**
 * ChatErrorBoundary — wraps the Ion UI so a render-time crash inside the
 * chat tree never tears down the rest of the application. Falls back to a
 * minimal recovery surface that lets the user dismiss or reload.
 *
 * Why a chat-specific boundary instead of the global ErrorBoundary? The chat
 * has its own state lifecycle (streaming, abort controllers, portal mount)
 * and we want to clear it on recovery, not just remount the same broken
 * subtree. See shared/ErrorBoundary for the generic version this mirrors.
 */

import { Component } from 'preact'
import { closeChat, clearConversation } from '../../state/chatStore.js'
import { t } from '../../i18n/index.js'

export class ChatErrorBoundary extends Component {
  state = { error: null }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    console.error('[ChatErrorBoundary] Ion crashed:', error, info)
  }

  handleDismiss = () => {
    this.setState({ error: null })
    closeChat()
  }

  handleReset = () => {
    this.setState({ error: null })
    clearConversation()
  }

  render() {
    if (this.state.error) {
      return (
        <div class="ion-error-boundary" role="alert">
          <div class="ion-error-boundary__icon" aria-hidden="true">!</div>
          <h3 class="ion-error-boundary__title">{t('ion.boundary.title')}</h3>
          <p class="ion-error-boundary__body">{t('ion.boundary.body')}</p>
          <div class="ion-error-boundary__actions">
            <button type="button" class="ion-btn" onClick={this.handleReset}>
              {t('ion.boundary.reset')}
            </button>
            <button type="button" class="ion-btn ion-btn--ghost" onClick={this.handleDismiss}>
              {t('ion.boundary.close')}
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
